/**
 * Client-side model of the HLB team board: platform companies are folded into
 * legal entities (same PIB, or a main company with its branches) and the
 * assignments of the entity's companies are summed up per function.
 */

export const FUNCTIONS = ['BOOKKEEPING', 'CONTROL', 'PAYROLL', 'PAYMENTS', 'REPORTS', 'ADMIN'];

export const FUNCTION_COLORS = {
  BOOKKEEPING: 'green',
  CONTROL: 'purple',
  PAYROLL: 'blue',
  PAYMENTS: 'gold',
  REPORTS: 'red',
  ADMIN: 'magenta',
};

// Hex of the same colours, for the small workload bars.
export const FUNCTION_BAR = {
  BOOKKEEPING: '#52c41a',
  CONTROL: '#722ed1',
  PAYROLL: '#1677ff',
  PAYMENTS: '#faad14',
  REPORTS: '#f5222d',
  ADMIN: '#eb2f96',
};

export const CLIENT_STATUSES = ['ACTIVE', 'FORMER', 'TEST', 'OWN'];
export const CLIENT_STATUS_COLORS = { ACTIVE: 'green', FORMER: 'default', TEST: 'orange', OWN: 'blue' };
export const CLIENT_FLAGS = ['CLOSING', 'IDLE'];
export const CONTACT_SECTIONS = ['PAYROLL', 'REPORTS', 'PAYMENTS', 'BOOKKEEPING', 'INVOICES'];

export const personName = (p) => (p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '?');

const groupKeyOf = (c) => {
  const pib = String(c.pib || '').trim();
  if (/^\d{9}$/.test(pib)) return `pib:${pib}`;
  return `main:${c.mainCompany || c._id}`;
};

// The company that names the entity: a main one, the one most people work in,
// then the shortest name.
const pickPrimary = (companies) =>
  [...companies].sort(
    (a, b) =>
      (a.mainCompany ? 1 : 0) - (b.mainCompany ? 1 : 0) ||
      (b.companyAdmins?.length || 0) - (a.companyAdmins?.length || 0) ||
      a.name.length - b.name.length
  )[0];

/**
 * Sums the assignments of one function across the entity's companies: one
 * entry per person and role, with how many of the companies it covers.
 */
const summarize = (rows, total) => {
  const byKey = new Map();
  rows.forEach((a) => {
    const key = `${a.employee}|${a.role}|${a.transition || ''}|${a.substitution ? 'S' : ''}`;
    const cur = byKey.get(key) || { ...a, companies: [], notes: new Set() };
    cur.companies.push(a.company);
    if (a.note) cur.notes.add(a.note);
    byKey.set(key, cur);
  });
  return [...byKey.values()]
    .map((x) => ({ ...x, count: x.companies.length, total, notes: [...x.notes] }))
    .sort(
      (x, y) =>
        (x.role === 'LEAD' ? 0 : 1) - (y.role === 'LEAD' ? 0 : 1) ||
        (x.transition === 'OUT' ? 0 : x.transition === 'IN' ? 1 : -1) -
          (y.transition === 'OUT' ? 0 : y.transition === 'IN' ? 1 : -1)
    );
};

// Two people leading the same function inside one company (not a hand-over).
const hasTwoLeads = (rows, fn) => {
  const perCompany = new Map();
  rows
    .filter((a) => a.function === fn && a.role === 'LEAD' && !a.transition)
    .forEach((a) => {
      const set = perCompany.get(a.company) || new Set();
      set.add(a.employee);
      perCompany.set(a.company, set);
    });
  return [...perCompany.values()].some((s) => s.size >= 2);
};

const NEW_COMPANY_DAYS = 30;

/** True when a hand-over's agreed end date is already behind us. */
export const isOverdue = (row) => {
  if (!row?.transition || !row.transitionUntil) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(row.transitionUntil) < today;
};

export const buildBoard = (board) => {
  if (!board) return { groups: [], people: [], peopleById: new Map(), substitutions: [] };
  const peopleById = new Map(board.people.map((p) => [p._id, p]));
  const userToPerson = new Map(board.people.filter((p) => p.userId).map((p) => [p.userId, p._id]));
  const byCompany = new Map();
  board.assignments.forEach((a) => {
    const list = byCompany.get(a.company) || [];
    list.push(a);
    byCompany.set(a.company, list);
  });

  const map = new Map();
  board.companies.forEach((c) => {
    const key = groupKeyOf(c);
    const g = map.get(key) || { key, companies: [] };
    g.companies.push(c);
    map.set(key, g);
  });

  const groups = [...map.values()].map((g) => {
    const primary = pickPrimary(g.companies);
    const ids = g.companies.map((c) => c._id);
    const rows = ids.flatMap((id) => byCompany.get(id) || []);
    const byFunction = Object.fromEntries(
      FUNCTIONS.map((fn) => [fn, summarize(rows.filter((a) => a.function === fn), ids.length)])
    );
    const hc = g.companies.map((c) => c.hlbClient.payrollHeadcount).filter((n) => n != null);
    // Per company, so a person on one project of a big client counts only that project.
    const headcountByCompany = new Map(g.companies.map((c) => [c._id, c.hlbClient.payrollHeadcount]));
    const assignedPeople = new Set(rows.map((a) => a.employee));
    // Team members in the portfolio of a company without any assignment there.
    const accessOnly = [
      ...new Set(
        g.companies.flatMap((c) =>
          (c.companyAdmins || [])
            .map((u) => userToPerson.get(u))
            .filter((pid) => pid && !(byCompany.get(c._id) || []).some((a) => a.employee === pid))
        )
      ),
    ];
    return {
      key: g.key,
      name: primary.name,
      primaryId: primary._id,
      companyIds: ids,
      companies: g.companies,
      size: ids.length,
      pib: primary.pib,
      clientStatus: primary.hlbClient.status,
      flags: [...new Set(g.companies.flatMap((c) => c.hlbClient.flags || []))],
      notes: [...new Set(g.companies.map((c) => c.hlbClient.note).filter(Boolean))],
      headcount: hc.length ? hc.reduce((s, n) => s + n, 0) : null,
      headcountByCompany,
      headcountNote: g.companies.map((c) => c.hlbClient.payrollHeadcountNote).filter(Boolean).join(' · '),
      paymentsBank: g.companies.map((c) => c.hlbClient.paymentsBank).find(Boolean) || '',
      paymentsMode: g.companies.map((c) => c.hlbClient.paymentsMode).find(Boolean) || '',
      contactsCount: g.companies.reduce((s, c) => s + (c.contactsCount || 0), 0),
      contactSections: [...new Set(g.companies.flatMap((c) => c.contactSections || []))],
      rows,
      byFunction,
      people: assignedPeople,
      accessOnly,
      // Someone who left HLB still holds work here: it has to be handed over.
      departed: rows.some((a) => peopleById.get(a.employee)?.left),
      transitionOverdue: rows.some(isOverdue),
      covered: rows.some((a) => a.substitution),
      // A company of the group (new project or sector) has no one while the rest do.
      newInGroup: ids.length > 1 && rows.length > 0 && g.companies.some((c) => !(byCompany.get(c._id) || []).length),
      isNew: g.companies.some((c) => c.createdAt && Date.now() - new Date(c.createdAt).getTime() < NEW_COMPANY_DAYS * 86400000),
      inTransition: rows.some((a) => a.transition),
      twoBookkeepers: hasTwoLeads(rows, 'BOOKKEEPING'),
      twoPayroll: hasTwoLeads(rows, 'PAYROLL'),
      noBookkeeper: !rows.some((a) => a.function === 'BOOKKEEPING') && rows.some((a) => a.function === 'CONTROL'),
      onlyPayroll: rows.length > 0 && !rows.some((a) => a.function === 'BOOKKEEPING' || a.function === 'CONTROL'),
      unassigned: rows.length === 0,
      issues: rows.filter((a) => a.confirmation === 'REPORTED' && !a.issueResolvedAt),
    };
  });
  groups.sort((a, b) => a.name.localeCompare(b.name, 'sr'));
  return { groups, people: board.people, peopleById, substitutions: board.substitutions || [] };
};

/** Quick filters above the table; `manager` ones only for managers. */
export const QUICK_FILTERS = [
  { key: 'all', test: () => true },
  { key: 'trans', test: (g) => g.inTransition },
  { key: 'transOverdue', test: (g) => g.transitionOverdue, warn: true },
  { key: 'covered', test: (g) => g.covered },
  { key: 'newInGroup', test: (g) => g.newInGroup, warn: true, manager: true },
  { key: 'two', test: (g) => g.twoBookkeepers, warn: true },
  { key: 'noBook', test: (g) => g.noBookkeeper, warn: true },
  { key: 'twoPay', test: (g) => g.twoPayroll, warn: true },
  { key: 'onlyPay', test: (g) => g.onlyPayroll },
  { key: 'unassigned', test: (g) => g.unassigned, warn: true },
  { key: 'closing', test: (g) => g.flags.length > 0 },
  { key: 'noContacts', test: (g) => g.contactsCount === 0 },
  { key: 'issues', test: (g) => g.issues.length > 0, warn: true, manager: true },
  { key: 'accessOnly', test: (g) => g.accessOnly.length > 0, manager: true },
];

/**
 * Payroll headcount a person carries on one entity: only the companies of the
 * entity where they lead payroll (one PCCC project, not all of PCCC). `null`
 * when none of those companies has a number.
 */
export const personHeadcountIn = (g, personId) => {
  const ids = [
    ...new Set(
      g.rows.filter((a) => a.employee === personId && a.function === 'PAYROLL' && a.role === 'LEAD' && a.transition !== 'OUT').map((a) => a.company)
    ),
  ];
  const nums = ids.map((id) => g.headcountByCompany?.get(id)).filter((n) => n != null);
  return nums.length ? nums.reduce((s, n) => s + n, 0) : null;
};

/** Per person: how many entities, by function, hand-overs and payroll headcount. */
export const personStats = (groups, personId, fn) => {
  const mine = groups.filter((g) => g.people.has(personId));
  const rows = mine.flatMap((g) => g.rows.filter((a) => a.employee === personId));
  const ofFn = fn ? mine.filter((g) => g.rows.some((a) => a.employee === personId && a.function === fn && a.transition !== 'OUT')) : mine;
  // Entities per function (a client with seven projects counts once); the raw
  // row counts only weigh the workload bar.
  const byFn = Object.fromEntries(
    FUNCTIONS.map((f) => [f, mine.filter((g) => g.rows.some((a) => a.employee === personId && a.function === f)).length])
  );
  const byFnRows = Object.fromEntries(FUNCTIONS.map((f) => [f, rows.filter((a) => a.function === f).length]));
  return {
    firms: mine.length,
    firmsOfFn: ofFn.length,
    rows: rows.length,
    byFn,
    byFnRows,
    out: new Set(rows.filter((a) => a.transition === 'OUT').map((a) => `${a.company}|${a.function}`)).size,
    in: new Set(rows.filter((a) => a.transition === 'IN').map((a) => `${a.company}|${a.function}`)).size,
    headcount: mine.reduce((s, g) => s + (personHeadcountIn(g, personId) || 0), 0),
    groupsOfFn: ofFn,
  };
};

/** Current or planned absence of a person: { active, planned } covers. */
export const absenceOf = (substitutions, personId) => ({
  active: substitutions.filter((x) => x.absent === personId && x.status === 'ACTIVE'),
  planned: substitutions.filter((x) => x.absent === personId && x.status === 'PLANNED'),
  covering: substitutions.filter((x) => x.substitute === personId && x.status === 'ACTIVE'),
});
